import { resolveYear } from './year.js';
import { rollVariableOutputs } from './variableOutput.js';
import { lookupCarbonBracket, pickCarbonMultiplier } from './carbonTax.js';
import {
  drawDisasters,
  assignRiskTiers,
  tierWeightsToCardWeights,
  resolvePersistentDisasterTarget
} from './disasters.js';
import { solveTierWeights } from './weightSolver.js';
import { GAME_YEARS, DISASTER_DRAWS_PER_YEAR } from '../data/constants.js';
import { CARBON_TAX_BRACKETS } from '../data/brackets.js';
import { COP30_OUTCOMES } from '../data/cop30.js';
import { EFFECT_KIND } from '../data/disasters.js';
import { GAME_MODE, CLASSIC_GAME_VERSION } from '../data/modes.js';
import {
  computeCleanEnergyAward,
  computeResilienceAward
} from './awards.js';

export function rollWorldEvents(worldRng, cop30Outcomes = COP30_OUTCOMES) {
  const cop30 = cop30Outcomes[worldRng.integer(0, cop30Outcomes.length)];
  return { cop30 };
}

export { drawDisasters };

function mergeFamilyOutput(yearResults) {
  const totals = {};
  for (const year of yearResults) {
    for (const [family, output] of Object.entries(year.familyOutput ?? {})) {
      totals[family] = (totals[family] ?? 0) + output;
    }
  }
  return totals;
}

export function resolveGame({
  buildPlan,
  worldEvents,
  archetypeRng,
  plantsById,
  disasterDeck,
  brackets = CARBON_TAX_BRACKETS,
  initialSubsidyActive = true
}) {
  const { cop30 } = worldEvents;

  let portfolio = [];
  let remainingDeck = [...disasterDeck];
  let subsidyActive = initialSubsidyActive;
  let persistentDisasters = [];
  const subsidyByYear = {};
  const yearResults = [];

  for (let year = 1; year <= GAME_YEARS; year++) {
    const newlyBuilt = buildPlan[year] ?? [];
    portfolio = [...portfolio, ...newlyBuilt];

    const rolledOutputs = rollVariableOutputs(
      portfolio,
      plantsById,
      archetypeRng
    );

    const drawCount = DISASTER_DRAWS_PER_YEAR[year];
    let weightByCardId = null;
    let tierByCardId = null;
    if (year > 1 && remainingDeck.length >= 4) {
      tierByCardId = assignRiskTiers(remainingDeck, archetypeRng);
      const tierWeights = solveTierWeights({
        poolSize: remainingDeck.length,
        draws: drawCount
      });
      weightByCardId = tierWeightsToCardWeights({
        pool: remainingDeck,
        tierByCardId,
        tierWeights
      });
    }
    const { drawn, remaining } = drawDisasters(
      remainingDeck,
      drawCount,
      archetypeRng,
      weightByCardId
    );
    remainingDeck = remaining;

    const subsidyReformed = drawn.some(
      (d) => d.effect.kind === EFFECT_KIND.SUBSIDY_REMOVE
    );
    if (subsidyReformed) subsidyActive = false;
    subsidyByYear[year] = subsidyActive;

    const resolvedDrawn = drawn.map((d) =>
      resolvePersistentDisasterTarget({
        disaster: d,
        portfolio,
        plantsById,
        rng: archetypeRng
      })
    );
    const newPersistentDisasters = resolvedDrawn.filter(
      (d) =>
        d.effect.kind === EFFECT_KIND.OUTPUT_MULTIPLIER &&
        d.effect.persistent &&
        d.persistentTargetFamily
    );
    persistentDisasters = [...persistentDisasters, ...newPersistentDisasters];

    const r = resolveYear({
      year,
      portfolio: [...portfolio],
      newlyBuiltThisYear: newlyBuilt,
      subsidyActive,
      disasters: [
        ...resolvedDrawn,
        ...persistentDisasters.filter(
          (d) => !newPersistentDisasters.some((fresh) => fresh.id === d.id)
        )
      ],
      rolledOutputs,
      plantsById,
      rng: archetypeRng
    });
    yearResults.push({ ...r, subsidyReformed, tierByCardId });
  }

  const totalCapital = yearResults.reduce((s, y) => s + y.capital, 0);
  const totalOpex = yearResults.reduce((s, y) => s + y.opex, 0);
  const totalShortfall = yearResults.reduce((s, y) => s + y.shortfall, 0);
  const totalShortfallCost = yearResults.reduce(
    (s, y) => s + y.shortfallCost,
    0
  );
  const totalEmissions = yearResults.reduce((s, y) => s + y.emissions, 0);
  const totalProducedOutput = yearResults.reduce(
    (s, y) => s + y.totalOutput,
    0
  );
  const totalCleanEnergyOutput = yearResults.reduce(
    (s, y) => s + y.cleanEnergyOutput,
    0
  );
  const totalVariableCleanEnergyOutput = yearResults.reduce(
    (s, y) => s + (y.variableCleanEnergyOutput ?? 0),
    0
  );
  const totalFamilyOutput = mergeFamilyOutput(yearResults);

  const bracket = lookupCarbonBracket(totalEmissions, brackets);
  const carbonMultiplier = pickCarbonMultiplier(bracket, cop30);
  const carbonAmount = bracket.base * carbonMultiplier;

  const cleanAward = computeCleanEnergyAward({
    eligibleCleanMWh: totalCleanEnergyOutput,
    variableCleanMWh: totalVariableCleanEnergyOutput,
    totalPostDisasterMWh: totalProducedOutput,
    portfolio,
    plantsById
  });
  const resilienceAwardResult = computeResilienceAward({
    portfolio,
    familyMWh: totalFamilyOutput,
    totalPostDisasterMWh: totalProducedOutput,
    plantsById
  });
  const cleanEnergyAward = cleanAward.amount;
  const resilienceAward = resilienceAwardResult.amount;
  const totalAwards = cleanEnergyAward + resilienceAward;

  const totalCost =
    totalCapital +
    totalOpex +
    totalShortfallCost -
    carbonAmount -
    totalAwards;

  return {
    mode: GAME_MODE.CLASSIC,
    gameVersion: CLASSIC_GAME_VERSION,
    totalCost,
    totalCapital,
    totalOpex,
    totalShortfall,
    totalShortfallCost,
    totalEmissions,
    bracket,
    cop30,
    carbonAmount,
    totalProducedOutput,
    totalCleanEnergyOutput,
    totalVariableCleanEnergyOutput,
    totalFamilyOutput,
    cleanEnergyShare: cleanAward.cleanShare,
    variableCleanEnergyShare: cleanAward.variableCleanShare,
    cleanEnergyAward,
    cleanEnergyAwardTier: cleanAward.tier,
    cleanEnergyAwardEligible: cleanAward.eligible,
    cleanEnergyAwardFamilies: cleanAward.distinctFamiliesBuilt,
    resilienceAward,
    resilienceAwardTier: resilienceAwardResult.tier,
    resilienceFamilies: resilienceAwardResult.distinctFamiliesBuilt,
    resilienceLargestFamilyShare: resilienceAwardResult.largestFamilyShare,
    totalAwards,
    subsidyByYear,
    yearResults,
    finalPortfolio: portfolio,
    hadShortfall: totalShortfall > 0
  };
}
